import Document from 'flexsearch/dist/module/document';
import GhostContentAPI, {Author, GhostAPI, PostOrPage, Tag} from '@tryghost/content-api';

const SEARCH_SCRIPT_SELECTOR = 'script[data-sodo-search]';
const API_URL_ATTRIBUTE = 'data-sodo-search';
const API_KEY_ATTRIBUTE = 'data-key';

const POSTS_CACHE_KEY = 'postsCache';
const AUTHORS_CACHE_KEY = 'authorsCache';
const TAGS_CACHE_KEY = 'tagsCache';
const CACHE_TIMESTAMP_SUFFIX = '_timestamp';
const MAX_CACHE_AGE = 15 * 60 * 1000; // 15 minutes in milliseconds

export class SearchManager {
  private api: GhostAPI;
  private postsIndex: Document<PostOrPage, true>;
  private authorsIndex: Document<Author, true>;
  private tagsIndex: Document<Tag, true>;

  constructor() {
    const { apiUrl, apiKey } = this.apiInfo;

    this.api = new GhostContentAPI({
      url: apiUrl,
      key: apiKey,
      version: 'v5.0'
    });

    const dir = document.documentElement.dir || 'ltr';
    const rtl = (dir === 'rtl');
    const tokenize = rtl ? 'reverse' : 'forward';

    this.postsIndex = new Document<PostOrPage, true>({
      tokenize: tokenize,
      document: {
        id: 'id',
        index: ['title', 'excerpt'],
        store: true
      },
      ...this.getEncodeOptions()
    });

    this.authorsIndex = new Document<Author, true>({
      tokenize: tokenize,
      document: {
        id: 'id',
        index: ['name'],
        store: true
      },
      ...this.getEncodeOptions()
    });

    this.tagsIndex = new Document<Tag, true>({
      tokenize: tokenize,
      document: {
        id: 'id',
        index: ['name'],
        store: true
      },
      ...this.getEncodeOptions()
    });

    this.init();
  }

  private get apiInfo() {
    const $script = document.querySelector(SEARCH_SCRIPT_SELECTOR);
    const url = $script?.getAttribute(API_URL_ATTRIBUTE);
    return {
      apiUrl: (url || window.location.origin)?.replace(/\/+$/, ''),
      apiKey: $script?.getAttribute(API_KEY_ATTRIBUTE),
    }
  }

  private async init() {
    const posts = await this.getCachedData(POSTS_CACHE_KEY, () => this.api.posts.browse({
      limit: '10000',
      fields: 'id,slug,title,excerpt,url,updated_at,visibility,feature_image',
      order: 'updated_at DESC'
    }));
    this.updatePostIndex(posts.filter(({ id }) => id));

    const authors = await this.getCachedData(AUTHORS_CACHE_KEY, () => this.api.authors.browse({
      limit: '10000',
      fields: 'id,slug,name,url,profile_image',
      order: 'updated_at DESC'
    }));
    this.updateAuthorsIndex(authors.filter(({ id }) => id));

    const tags = await this.getCachedData(TAGS_CACHE_KEY, () => this.api.tags.browse({
      limit: '10000',
      fields: 'id,slug,name,url',
      order: 'updated_at DESC',
      filter: 'visibility:public'
    }));
    this.updateTagsIndex(tags.filter(({ id }) => id));
  }

  private async getCachedData<T>(cacheKey: string, fetchFunction: () => Promise<T>): Promise<T> {
    const cachedData = sessionStorage.getItem(cacheKey);
    const cachedTimestamp = sessionStorage.getItem(cacheKey + CACHE_TIMESTAMP_SUFFIX);
    const now = Date.now();

    if (cachedData && cachedTimestamp) {
      const cacheAge = now - parseInt(cachedTimestamp, 10);
      if (cacheAge < MAX_CACHE_AGE) {
        return JSON.parse(cachedData);
      }
    }

    const data = await fetchFunction();
    sessionStorage.setItem(cacheKey, JSON.stringify(data));
    sessionStorage.setItem(cacheKey + CACHE_TIMESTAMP_SUFFIX, now.toString());
    return data;
  }

  private updatePostIndex(posts: PostOrPage[]) {
    posts.forEach((post) => {
      this.postsIndex.add(post);
    });
  }

  private updateAuthorsIndex(authors: Author[]) {
    authors.forEach((author) => {
      this.authorsIndex.add(author);
    });
  }

  private updateTagsIndex(tags: Tag[]) {
    tags.forEach((tag) => {
      this.tagsIndex.add(tag);
    });
  }

  private normalizeSearchResult(result: any[]) {
    const normalized = [];
    const usedIds: {[key: string]: boolean} = {};

    result.forEach((resultItem) => {
      resultItem.result.forEach((doc: any) => {
        if (!usedIds[doc.id]) {
          normalized.push(doc.doc);
          usedIds[doc.id] = true;
        }
      });
    });

    return normalized;
  }

  search = (query: string) => {
    const postsResult = this.postsIndex.search(query, { enrich: true });
    const authorsResult = this.authorsIndex.search(query, { enrich: true });
    const tagsResult = this.tagsIndex.search(query, { enrich: true });

    return {
      posts: this.normalizeSearchResult(postsResult),
      authors: this.normalizeSearchResult(authorsResult),
      tags: this.normalizeSearchResult(tagsResult)
    };
  }

  private getEncodeOptions() {
    const regex = new RegExp(
      `[\u{4E00}-\u{9FFF}\u{3040}-\u{309F}\u{30A0}-\u{30FF}\u{AC00}-\u{D7A3}\u{3400}-\u{4DBF}\u{20000}-\u{2A6DF}\u{2A700}-\u{2B73F}\u{2B740}-\u{2B81F}\u{2B820}-\u{2CEAF}\u{2CEB0}-\u{2EBEF}\u{30000}-\u{3134F}\u{31350}-\u{323AF}\u{2EBF0}-\u{2EE5F}\u{F900}-\u{FAFF}\u{2F800}-\u{2FA1F}]|[0-9A-Za-zа-я\u00C0-\u017F\u0400-\u04FF\u0600-\u06FF\u0980-\u09FF\u1E00-\u1EFF]+`,
      'mug'
    );

    return {
      encode: (str: string) => {
        return ('' + str).toLowerCase().match(regex) ?? [];
      }
    };
  }
}